// Signature scenes (×3, data-driven) and the shared PinnedScene wrapper:
// a photo pinned to the viewport that develops on entry, then crops into a
// framed plate as the copy panel scrolls over it. All per-frame motion is
// written by useScrollEngine via the data-* hooks below.
// Port of prototype lines 67–163.
import { SIZES, srcSet } from './data/photos.js';

const mono = { fontFamily: "'DM Mono', 'DM Mono Fallback', ui-monospace, monospace" };
const serif = { fontFamily: "'Instrument Serif', Georgia, serif", fontStyle: 'italic', fontWeight: 400 };
const TITLE_GRADIENT = 'linear-gradient(180deg, rgba(7,30,33,0) 0%, rgba(7,30,33,.6) 32%, rgba(7,30,33,.94) 100%)';
const BLANK = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='; // 1×1 transparent, until the engine sets src

// Scene palettes (handoff tokens). Kept here — the data-file palettes belong to the book.
const SCENE_PAL = {
  's-sig': { bg: '#EFE7D5', ink: '#1A1410', eyebrow: '#8E4422', italic: '#A85328', rule: 'rgba(26,20,16,.16)', rowRule: 'rgba(26,20,16,.1)', muted: '#5C4A3E' },
  's-matcha': { bg: '#0F5049', ink: '#F5F3EB', eyebrow: '#D4C4A6', italic: '#D4C4A6', rule: 'rgba(245,243,235,.22)', rowRule: 'rgba(245,243,235,.12)', muted: '#D4C4A6' },
  's-mont': { bg: '#F5EFE3', ink: '#2A1B14', eyebrow: '#8A5E1C', italic: '#8A5E1C', rule: 'rgba(42,27,20,.16)', rowRule: 'rgba(42,27,20,.1)', muted: '#5C4A3E' },
};

/**
 * Pinned photo + scrolling copy panel.
 * @param {{ id:string, pageTone:'day'|'night', bg:string, photo:{src:string,focus:string,alt:string}, title:React.ReactNode, copyStyle:object, label:string, priority?:boolean, children:React.ReactNode }} p
 *   priority — this scene is the deep-link landing: fetch its photo first; other scenes' photos load lazily.
 */
export function PinnedScene({ id, pageTone, bg, photo, title, copyStyle, label, priority, children }) {
  return (
    <section
      id={id}
      data-stage-sec
      data-tone="night"
      data-page-tone={pageTone}
      aria-label={label}
      style={{ position: 'relative', zIndex: 1, scrollSnapAlign: 'start', background: bg }}
    >
      {/* Full viewport height. A shorter cover was tried to reduce the crop and
          it leaves the page colour showing in the gap underneath — on a phone
          that reads as a leak, not as composition. The crop is set by the
          photo's own aspect ratio; the way to show more of it is a taller
          source crop, not a shorter frame. */}
      <div data-scene-photo style={{ position: 'sticky', top: 0, height: 'var(--vh, 844px)', overflow: 'hidden', background: bg, touchAction: 'pan-y' }}>
        <div data-photo style={{ position: 'absolute', inset: 0, willChange: 'clip-path' }}>
          {/* Non-landing photos carry data-src: the engine assigns src when the
              scene comes within a quarter screen, so a deep link's own hero never
              competes with its neighbours' (native lazy still fetched the scene
              above, whose last strip sits under the sticky header). */}
          <img
            src={priority ? photo.src : BLANK} srcSet={priority ? srcSet(photo.src) : undefined} sizes={SIZES}
            data-src={priority ? undefined : photo.src} data-srcset={priority ? undefined : srcSet(photo.src)}
            alt={photo.alt} decoding="async" fetchpriority={priority ? 'high' : undefined}
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: photo.focus, display: 'block', willChange: 'transform' }} />
        </div>
        <div data-title style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '7em 1.5em 2.4em', background: TITLE_GRADIENT, color: '#f4f0e8', display: 'flex', flexDirection: 'column', gap: '.6em' }}>
          {title}
        </div>
      </div>
      <div data-copy data-on="0" style={{ position: 'relative', zIndex: 2, padding: '1.6em 1.5em 2em', display: 'flex', flexDirection: 'column', gap: '.7em', ...copyStyle }}>
        {children}
      </div>
    </section>
  );
}

export function Eyebrow({ color, children, delay }) {
  return <span className="beat" style={{ ...mono, fontSize: '.6875em', letterSpacing: '.14em', textTransform: 'uppercase', color, transitionDelay: delay }}>{children}</span>;
}

export function Accent({ color, children }) {
  return <em style={{ ...serif, fontSize: '1.06em', color }}>{children}</em>;
}

export default function SignatureScene({ sig, index, T, priority }) {
  const P = SCENE_PAL[sig.id] || SCENE_PAL['s-sig'];
  const n = String(index + 1).padStart(2, '0');
  const rows = sig.serviceRows || [];
  const steps = sig.steps || [];
  const serviceLabel = sig.serviceLabel === 'Service' ? T.service : T.servedAs;

  const title = (
    <>
      <span style={{ ...mono, fontSize: '.6875em', letterSpacing: '.14em', textTransform: 'uppercase', color: '#e8b86b' }}>{T.chapSig} · {n} / 03</span>
      <span style={{ fontSize: '3em', fontWeight: 500, letterSpacing: '-.04em', lineHeight: 0.95, textWrap: 'balance' }}>{sig.headline} <Accent color="#e8b86b">{sig.headlineItalic}</Accent></span>
      <span style={{ fontSize: '1.0625em', lineHeight: 1.4, maxWidth: '19em', textWrap: 'pretty' }}>{sig.scene.one}</span>
      <span style={{ ...mono, fontWeight: 500, fontSize: '1.125em', letterSpacing: '.02em' }}>{sig.scene.priceLine}</span>
    </>
  );

  return (
    <PinnedScene
      id={sig.id}
      pageTone={sig.pageTone}
      bg={P.bg}
      photo={sig.scene}
      title={title}
      priority={priority}
      label={`${sig.headline} ${sig.headlineItalic}`}
      copyStyle={{ background: P.bg, color: P.ink, borderTop: `1px solid ${P.rule}` }}
    >
      <Eyebrow color={P.eyebrow}>{T.house} · {n} / 03</Eyebrow>
      <span className="beat" role="heading" aria-level={2} style={{ fontSize: '2.4em', fontWeight: 500, letterSpacing: '-.04em', lineHeight: 0.98, transitionDelay: '.08s' }}>
        {sig.headline} <Accent color={P.italic}>{sig.headlineItalic}</Accent>
      </span>
      <span className="beat" style={{ fontSize: '1.0625em', lineHeight: 1.45, textWrap: 'pretty', transitionDelay: '.2s' }}>{sig.lede}</span>

      {sig.originMark && (
        <div className="beat" style={{ display: 'flex', alignItems: 'center', gap: 12, transitionDelay: '.25s' }}>
          <span style={{ ...serif, fontStyle: 'normal', fontSize: '1.6em', lineHeight: 1 }} lang="ja">{sig.originMark.characters}</span>
          <span aria-hidden="true" style={{ width: 4, height: 4, borderRadius: '50%', background: P.eyebrow }} />
          <span style={{ ...mono, fontSize: '.6875em', letterSpacing: '.22em', textTransform: 'uppercase', color: P.eyebrow, whiteSpace: 'nowrap' }}>{sig.originMark.label.replace(/ · /g, ' · ')}</span>
        </div>
      )}

      {steps.length > 0 && (
        <div className="beat" style={{ position: 'relative', paddingLeft: 24, marginTop: '.4em', transitionDelay: '.3s' }}>
          <span aria-hidden="true" style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 1, background: P.rule }} />
          <span aria-hidden="true" style={{ position: 'absolute', left: 0, top: 0, width: 1, height: 'calc(var(--rail, 1) * 100%)', background: P.italic }} />
          {steps.map((s, i) => (
            <div
              key={i}
              style={{ display: 'flex', gap: 14, alignItems: 'flex-start', padding: '.6em 0', borderBottom: i < steps.length - 1 ? `1px solid ${P.rowRule}` : 0, opacity: `clamp(.3, calc((var(--rail, 1) - ${[0, 0.22, 0.44, 0.66][i] ?? 0.66}) * 6 + .3), 1)` }}
            >
              <span style={{ ...mono, fontSize: '.6875em', lineHeight: 1.7, color: P.eyebrow, width: '2.2em', flex: 'none' }}>{['i', 'ii', 'iii', 'iv'][i]}</span>
              <span style={{ display: 'flex', flexDirection: 'column', gap: '.15em', minWidth: 0 }}>
                <b style={{ fontWeight: 500, fontSize: '1em', lineHeight: 1.3 }}>{s.title}</b>
                {s.note ? <span style={{ fontSize: '.9375em', lineHeight: 1.4, color: P.muted, textWrap: 'pretty' }}>{s.note}</span> : null}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="beat" style={{ display: 'flex', flexDirection: 'column', marginTop: '.4em', borderTop: `1px solid ${P.rule}`, transitionDelay: '.4s' }}>
        <span style={{ ...mono, fontSize: '.625em', letterSpacing: '.14em', textTransform: 'uppercase', color: P.eyebrow, padding: '.9em 0 .2em' }}>{serviceLabel}</span>
        {rows.map((r, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 16, padding: '.7em 0', borderBottom: `1px solid ${P.rule}` }}>
            <span style={{ display: 'flex', flexDirection: 'column', gap: '.2em' }}>
              <span style={{ fontSize: '1.125em', fontWeight: 500, letterSpacing: '-.01em' }}>{r.label}</span>
              {r.sub ? <span style={{ ...mono, fontSize: '.6875em', letterSpacing: '.04em', color: P.eyebrow, whiteSpace: 'nowrap' }}>{r.sub}</span> : null}
            </span>
            <span style={{ ...mono, fontWeight: 500, fontSize: '1.125em' }}>{r.price}</span>
          </div>
        ))}
      </div>

      {sig.pull && <span className="beat" style={{ ...serif, fontSize: '1.3em', lineHeight: 1.3, color: P.eyebrow, textWrap: 'pretty', paddingTop: '.3em', transitionDelay: '.5s' }}>{sig.pull}</span>}
      {sig.allergens && <span className="beat" style={{ fontSize: '.875em', lineHeight: 1.45, color: P.muted, transitionDelay: '.5s' }}>{sig.allergens}</span>}

      <div className="beat" style={{ display: 'flex', justifyContent: 'space-between', ...mono, fontSize: '.625em', letterSpacing: '.12em', textTransform: 'uppercase', color: P.muted, paddingTop: '.8em', transitionDelay: '.6s' }}>
        <span>{T.tabs.sig}</span><span>{n}</span>
      </div>
    </PinnedScene>
  );
}
